import apiRoutes from "../../Routes/Routes.json";
import HttpClient from "HttpClient";

export type IdLisRunTemplateOption = {
  value: string;
  label: string;
};

export type IdLisRunTemplatesResponse = {
  PlateSetup: IdLisRunTemplateOption[];
};

export type CreateIdLisRunRequest = {
  plateSetupId: number;
  templateName: string;
};

export type IdLisRunCreateResponse = {
  idLISRunId: number;
  plateSetupId?: number | null;
  runName?: string | null;
  runID?: string | null;
  runDate?: string | null;
  runBarcodeNo?: string | null;
  detailCount: number;
};

export type IdLisRunDetailResponse = {
  runDetailId: number;
  idLISRunId?: number | null;
  wellPosition?: string | null;
  wellName?: string | null;
  wellAlphabets?: string | null;
  wellNumber?: number | null;
  requisitionOrderId?: number | null;
  accessionNumber?: string | null;
  assignTestJson?: string | null;
};

export type IdLisRunListResponse = {
  idLISRunId: number;
  plateSetupId?: number | null;
  runName?: string | null;
  runID?: string | null;
  runDate?: string | null;
  runBarcodeNo?: string | null;
  runStatus?: string | null;
  lastRunStatus?: string | null;
  createdBy?: string | null;
  createdDate?: string | null;
  plateTypeId?: string | null;
  plateTemplate?: string | null;
  outputTemplate?: string | null;
  panelId?: number | null;
  panelName?: string | null;
  /** Number of accession bands across the plate (columns group under each). */
  noOfAccession?: string | number | null;
  rowCount: number;
  columnCount: number;
  totalWells: number;
  rows: string[];
  columns: number[];
  details: IdLisRunDetailResponse[];
};

export type RequestResponse<T> = {
  statusCode: number;
  message?: string | null;
  data: T;
};

export type ApplyIdLisRunAccessionRequest = {
  idLISRunId: number;
  accessionSlotIndex: number;
  accessionNumber: string;
};

export type ApplyIdLisRunAccessionsRequest = {
  idLISRunId: number;
  accessions: {
    accessionSlotIndex: number;
    accessionNumber: string;
  }[];
};

export type IdLisRunAccessionValidationItemResponse = {
  accessionSlotIndex: number;
  accessionNumber?: string | null;
  isValid: boolean;
  message?: string | null;
  requisitionOrderId?: number | null;
  updatedWellCount: number;
};

export type IdLisRunBulkAccessionUpdateResponse = {
  idLISRunId: number;
  totalSubmitted: number;
  validCount: number;
  invalidCount: number;
  items: IdLisRunAccessionValidationItemResponse[];
};

export type IdLisRunAccessionUpdateResponse = {
  idLISRunId: number;
  accessionSlotIndex: number;
  accessionNumber?: string | null;
  requisitionOrderId: number;
  updatedWellCount: number;
};

export type CompleteIdLisRunRequest = {
  idLISRunId: number;
};

export type SoftDeleteIdLisRunRequest = {
  idLISRunId: number;
};

/**
 * Filter fields inside `DataQueryModel<IdLisRunQueryModel>.queryModel`.
 */
export type IdLisRunQueryModel = {
  runId?: string | null;
  runName?: string | null;
  /** Run Type column (panel name in list UI). */
  panelName?: string | null;
  /** Run Template (plate template / output template text). */
  runTemplate?: string | null;
  runDate?: string | null;
  /** Added By (created-by id or display filter string). */
  addedBy?: string | null;
  plateBarcodeNo?: string | null;
  /**
   * Tab scope: `false` = active (non-complete) runs, `true` = complete runs only.
   * `null` = do not filter by completion (e.g. filter dropdown source query).
   */
  completeRunsOnly?: boolean | null;
};

/**
 * POST `[HttpPost("runs")]` body — matches backend `DataQueryModel<IdLisRunQueryModel>`.
 *
 * Example:
 * {
 *   "pageNumber": 1,
 *   "pageSize": 50,
 *   "sortColumn": "runName",
 *   "sortDirection": "asc",
 *   "queryModel": { ... }
 * }
 */
export type IdLisRunDataQueryRequest = {
  pageNumber: number;
  pageSize: number;
  sortColumn?: string | null;
  sortDirection?: string | null;
  queryModel: IdLisRunQueryModel;
};

/** Alias — same shape as {@link IdLisRunDataQueryRequest}. */
export type IdLisRunSearchRequest = IdLisRunDataQueryRequest;

export type IdLisRunPagedResponse = {
  statusCode: number;
  total: number;
  data: IdLisRunListResponse[];
};

export type IdLisRunSearchFilters = {
  runId: string;
  runName: string;
  runType: string;
  runTemplate: string;
  runDate: string;
  addedBy: string;
  plateBarcodeNo: string;
};

export type IdLisRunSortState = {
  clickedIconData: string | null;
  sortingOrder: string;
};

export const emptyIdLisRunSearchFilters: IdLisRunSearchFilters = {
  runId: "",
  runName: "",
  runType: "",
  runTemplate: "",
  runDate: "",
  addedBy: "",
  plateBarcodeNo: "",
};

/**
 * Builds the POST body for `/api/idlis-run/runs` (`DataQueryModel<IdLisRunQueryModel>`).
 *
 * `sortColumn` values from the grid: `id`, `runName`, `runType`, `runTemplate`,
 * `runDate`, `addedBy`, `plateBarcodeNo`.
 */
export function buildIdLisRunSearchPayload(params: {
  pageNumber: number;
  pageSize: number;
  sort: IdLisRunSortState;
  filters: IdLisRunSearchFilters;
  /** `0` = View Runs, `1` = Complete Runs, `2` = Deleted Runs, `<0` = all (completeRunsOnly null). */
  tabIndex: number;
}): IdLisRunDataQueryRequest {
  const { pageNumber, pageSize, sort, filters, tabIndex } = params;
  const completeRunsOnly =
    tabIndex < 0 ? null : tabIndex === 0 ? false : tabIndex === 1 ? true : null;

  return {
    pageNumber,
    pageSize,
    sortColumn: sort.clickedIconData ?? null,
    sortDirection: sort.sortingOrder?.trim() || null,
    queryModel: {
      runId: filters.runId.trim() || null,
      runName: filters.runName.trim() || null,
      panelName: filters.runType.trim() || null,
      runTemplate: filters.runTemplate.trim() || null,
      runDate: filters.runDate.trim() || null,
      addedBy: filters.addedBy.trim() || null,
      plateBarcodeNo: filters.plateBarcodeNo.trim() || null,
      completeRunsOnly,
    },
  };
}

/** Large unfiltered page for Run Type / Run Template dropdown labels. */
export function buildIdLisRunRunsLookupPayload(): IdLisRunDataQueryRequest {
  return buildIdLisRunSearchPayload({
    pageNumber: 1,
    pageSize: 2000,
    sort: { clickedIconData: null, sortingOrder: "" },
    filters: emptyIdLisRunSearchFilters,
    tabIndex: -1,
  });
}

const GetRunTemplates = () => {
  return HttpClient().get<IdLisRunTemplatesResponse>(
    `/${apiRoutes.InfectiousDisease.GetIdLisRunTemplates}`
  );
};

const CreateRun = (payload: CreateIdLisRunRequest) => {
  return HttpClient().post<RequestResponse<IdLisRunCreateResponse>>(
    `/${apiRoutes.InfectiousDisease.CreateIdLisRun}`,
    payload
  );
};

/** `[HttpPost("runs")]` — body is `DataQueryModel<IdLisRunQueryModel>`. */
const GetRuns = (payload: IdLisRunDataQueryRequest) => {
  return HttpClient().post<IdLisRunPagedResponse>(
    `/${apiRoutes.InfectiousDisease.GetIdLisRuns}`,
    payload
  );
};

/** `[HttpPost("deleted-runs")]` — body is `DataQueryModel<IdLisRunQueryModel>`. */
const GetDeletedRuns = (payload: IdLisRunDataQueryRequest) => {
  return HttpClient().post<IdLisRunPagedResponse>(
    `/${apiRoutes.InfectiousDisease.GetDeletedIdLisRuns}`,
    payload
  );
};

const ApplyAccession = (payload: ApplyIdLisRunAccessionRequest) => {
  return HttpClient().post<RequestResponse<IdLisRunAccessionUpdateResponse>>(
    `/${apiRoutes.InfectiousDisease.ApplyIdLisRunAccession}`,
    payload
  );
};

const ApplyAccessions = (payload: ApplyIdLisRunAccessionsRequest) => {
  return HttpClient().post<
    RequestResponse<IdLisRunBulkAccessionUpdateResponse>
  >(`/${apiRoutes.InfectiousDisease.ApplyIdLisRunAccessions}`, payload);
};

const CompleteRun = (payload: CompleteIdLisRunRequest) => {
  return HttpClient().post<RequestResponse<boolean>>(
    `/${apiRoutes.InfectiousDisease.CompleteIdLisRun}`,
    payload
  );
};

const SoftDeleteRun = (payload: SoftDeleteIdLisRunRequest) => {
  return HttpClient().post<RequestResponse<boolean>>(
    `/${apiRoutes.InfectiousDisease.SoftDeleteIdLisRun}`,
    payload
  );
};

const IdLisRunService = {
  GetRunTemplates,
  CreateRun,
  GetRuns,
  GetDeletedRuns,
  ApplyAccession,
  ApplyAccessions,
  CompleteRun,
  SoftDeleteRun,
};

export default IdLisRunService;
